import { AffiliateProfileResponse } from '@avanzu/types';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/apiClient';

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<AffiliateProfileResponse | null>(null);
  const [businessName, setBusinessName] = useState('');
  const [website, setWebsite] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(
    null
  );

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await apiClient.get('/affiliate/profile');
        if (response.ok) {
          const data: AffiliateProfileResponse = await response.json();
          setProfile(data);
          setBusinessName(data.affiliate?.businessName ?? '');
          setWebsite(data.affiliate?.website ?? '');
          setPhone(data.affiliate?.phone ?? '');
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const response = await apiClient.put('/affiliate/profile', {
        businessName,
        website,
        phone,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Failed to save profile');
      }
      setMessage({ severity: 'success', text: 'Profile updated' });
    } catch (err) {
      setMessage({
        severity: 'error',
        text: err instanceof Error ? err.message : 'Failed to save profile',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <PaperSection title="Account">
        <Typography variant="body1">
          {user?.firstName} {user?.lastName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {user?.email}
        </Typography>
        {!user?.emailVerified && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Your email address is not verified yet — check your inbox for the verification link.
          </Alert>
        )}
      </PaperSection>

      {profile?.affiliate ? (
        <PaperSection title="Business details">
          {message && (
            <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSave}>
            <Stack spacing={2} sx={{ maxWidth: 480 }}>
              <TextField
                label="Business name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                size="small"
              />
              <TextField
                label="Website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://"
                size="small"
              />
              <TextField
                label="Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                size="small"
              />
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                sx={{ alignSelf: 'flex-start' }}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          </Box>
        </PaperSection>
      ) : (
        <PaperSection title="Business details">
          <Typography variant="body2" color="text.secondary">
            Apply to the affiliate program to add your business details.
          </Typography>
        </PaperSection>
      )}
    </Stack>
  );
};

export default ProfilePage;
